import { useState, useEffect } from 'react';

import { SupervisedUserCircleOutlined, Add as AddIcon, InfoOutlined } from '@mui/icons-material';
import {
    MenuItem,
    Stack,
    Typography,
    Switch,
    FormControlLabel,
    Box,
    Tooltip,
    Radio,
    RadioGroup,
    FormControl,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router';

import ABTestLabelBox from 'domains/campaigns/components/ABTestLabelBox';
import InfoAlertBox from 'domains/campaigns/components/CustomCampaign/components/InfoAlertBox';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    selectAudience,
    setCohortId,
    setABTestConfig,
    setABTestPercentage,
    removeABTestConfig,
    resetSends,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import {
    CampaignChannelLabels,
    AudienceType,
    CampaignChannels,
    ABTestConfig,
    SendRecurrenceFrequency,
} from 'domains/campaigns/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import Button from 'domains/core/components/Button';
import FormLayout from 'domains/core/components/FormLayout';
import WarningSnackbar from 'domains/core/components/Snackbars/WarningSnackbar';
import { Segment } from 'domains/segments/types';
import { hasInvalidRelativeDates } from 'domains/segments/utils';
import useAudienceCount, {
    UseAudienceCountResponse,
    UseAudienceCountWithChannelResponse,
} from 'hooks/queries/useAudienceCount';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useSegmentCohort from 'hooks/queries/useSegmentCohort';
import useSegmentCohorts from 'hooks/queries/useSegmentCohorts';
import useSegments from 'hooks/queries/useSegments';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useQueryParams from 'hooks/useQueryParams';
import { URLPaths, CampaignStatus } from 'models/enums';
import { getAudienceLabel } from 'utils';

import AudienceTypeSelector from './AudienceTypeSelector';

// A/B Test tooltip messages
const abTestCohortsValidationTooltip = 'A/B testing is available only for saved audiences, not cohorts.';
const audienceSelectionTooltipMessage = 'Please select an audience first to enable A/B testing.';
const insufficientAudienceSizeTooltip = 'A/B testing requires an audience of more than 1000 contacts.';
const splitAndCompareTooltipMessage =
    'Split your audience into two equal groups to test different email variations and compare performance.';
const insufficientAudienceFor50PercentSplit = 'The 50% split option requires an audience of more than 2000 contacts.';
const editCurationABTestTooltip = "You can't enable A/B Testing while editing a curation";
const cohortContinualScheduleTooltip = 'Switching to cohort is not allowed for continual sends';

const renderOption = ({
    history,
    option,
    props,
}: {
    history: ReturnType<typeof useHistory>;
    option: AutocompleteOption<number>;
    props: React.HTMLAttributes<HTMLLIElement>;
}) => {
    const { index, label, value } = option;
    const isCreateNewAudienceOption = index === 0;

    if (isCreateNewAudienceOption) {
        return (
            <MenuItem {...props} onClick={() => history.push(URLPaths.SEGMENT_CREATE)}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>Create New</Typography>
                    <AddIcon fontSize="inherit" />
                </Stack>
            </MenuItem>
        );
    }
    return <MenuItem {...props} children={label} key={value} />;
};

// Reusable component for audience split rows
const AudienceSplitRow = ({
    label,
    text,
    percentage,
    contactCount,
}: {
    label: string;
    text: string;
    percentage: string;
    contactCount: number | null;
}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
        <Box sx={{ width: 32, display: 'flex', justifyContent: 'flex-start' }}>
            <ABTestLabelBox label={label} size="small" />
        </Box>
        <Box sx={{ width: 120, display: 'flex', justifyContent: 'flex-start' }}>
            <Typography variant="body2">{text}</Typography>
        </Box>
        <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-start' }}>
            <Typography variant="body2">{percentage}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Typography variant="body2" color="GrayText" noWrap>
                {contactCount ? `${contactCount} contacts` : ''}
            </Typography>
        </Box>
    </Box>
);

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignAudienceStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setIsContinueEnabled,
    setNextStep,
}: Props) => {
    const { data: featureSettings } = useFeatureSettings();
    const { canCurationsAbTesting } = useAccessCentrePermissions();
    const { accessControlLayer: accessControlLayerFlag } = useFlags();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const disableABTestEditing = status === CampaignStatus.PAUSED;

    const isPlatformIntegrated = featureSettings?.platformIntegrated;
    const hasAccessControlLayer = featureSettings?.acl_enabled && accessControlLayerFlag;
    // Determine visibility scenarios
    const shouldShowOnlyCohorts = isPlatformIntegrated && hasAccessControlLayer;
    const shouldShowOnlyAudiences = !isPlatformIntegrated;
    const shouldShowBothOptions = isPlatformIntegrated && !hasAccessControlLayer;

    const history = useHistory();

    const firstSend = campaignState.sends[0] as any;
    const { segmentId, cohortId } = firstSend;
    const recurrenceFrequency = firstSend?.recurrenceFrequency;
    const currentSend = campaignState.sends[activeSendIndex];
    const { channel } = currentSend;

    // Check if schedule is continuous (for cohort disabling logic)
    const isContinuousSchedule = recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS;
    const isEditingPausedCuration = status === CampaignStatus.PAUSED;

    const [audienceType, setAudienceType] = useState<AudienceType>(() => {
        if (shouldShowOnlyCohorts) return AudienceType.COHORT;
        if (shouldShowOnlyAudiences) return AudienceType.AUDIENCE;
        // For shouldShowBothOptions, use existing logic
        return cohortId ? AudienceType.COHORT : AudienceType.AUDIENCE;
    });

    // For paused curations with continuous schedule: disable cohort selection with tooltip
    // Only show tooltip when editing paused curations (not for draft)
    const shouldDisableCohort =
        isEditingPausedCuration && isContinuousSchedule && audienceType === AudienceType.AUDIENCE;
    const shouldShowCohortTooltip = isEditingPausedCuration && isContinuousSchedule;

    const [audienceDropdownItems, setAudienceDropdownItems] = useState<AutocompleteOption<number | string>[]>();
    const [selectedAudienceOption, setSelectedAudienceOption] = useState<AutocompleteOption<number | string>>();
    const [inputValue, setInputValue] = useState('');
    const [isABTestActive, setIsABTestActive] = useState(!!campaignState.abTestConfig);
    const [testPercentage, setTestPercentage] = useState<'50' | '100'>(
        campaignState?.abTestConfig?.testPercentage === 50 ? '50' : '100'
    );

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { isSuccess: isAudiencesSuccess, data: audiences, isInitialLoading: isAudiencesLoading } = useSegments({
        enabled: shouldShowOnlyAudiences || (shouldShowBothOptions && audienceType === AudienceType.AUDIENCE),
    });

    const {
        isSuccess: isCohortsSuccess,
        data: cohorts,
        isInitialLoading: isCohortsLoading,
        refetch: refetchCohorts,
    } = useSegmentCohorts({
        reactQueryOptions: {
            enabled: shouldShowOnlyCohorts || (shouldShowBothOptions && audienceType === AudienceType.COHORT),
            // Keep previous data while new data is loading due to debounced search term change
            // This can improve UX by not clearing options immediately
            keepPreviousData: true,
            onError: () => {
                setErrorMessage(
                    `Cohorts from Cohort Builder couldn’t be fetched. You can still continue with saved audiences or reload.`
                );
            },
            onSuccess: () => setErrorMessage(null),
        },
    });

    const { data, isError: isAudienceCountError, isFetching: isAudienceCountFetching } = useAudienceCount(
        segmentId,
        channel,
        {
            enabled: Boolean(segmentId) && !campaignState.isMixedChannel,
        }
    );
    const audienceCountData = data as UseAudienceCountWithChannelResponse;

    // For mixed-channel campaigns, fetch the no-channel count separately so we get both
    // email and sms breakdowns in a single response ({ all, email, sms }).
    const { data: mixedCountRaw, isError: isMixedCountError, isFetching: isMixedCountFetching } = useAudienceCount(
        segmentId,
        undefined,
        {
            enabled: Boolean(segmentId) && !!campaignState.isMixedChannel,
        }
    );
    const mixedAudienceCountData = mixedCountRaw as UseAudienceCountResponse;

    const { data: cohortData, isError: isCohortError, isFetching: isCohortFetching } = useSegmentCohort(cohortId, {
        enabled: Boolean(cohortId),
    });

    // A/B testing conditions
    const audienceCountNumber =
        typeof audienceCountData?.count === 'string' ? parseInt(audienceCountData.count, 10) : audienceCountData?.count;
    const isABTestingFullyAvailable =
        channel === CampaignChannels.EMAIL &&
        audienceType === AudienceType.AUDIENCE &&
        selectedAudienceOption &&
        audienceCountNumber > 1000;

    const is50PercentSplitAvailable = audienceCountNumber > 2000;

    const getABTestTooltipMessage = () => {
        if (audienceType !== AudienceType.AUDIENCE) {
            return abTestCohortsValidationTooltip;
        }
        if (!selectedAudienceOption) {
            return audienceSelectionTooltipMessage;
        }
        if (audienceCountNumber <= 1000) {
            return insufficientAudienceSizeTooltip;
        }
        return splitAndCompareTooltipMessage;
    };

    const abTestTooltipMessage = getABTestTooltipMessage();

    useEffect(() => {
        if (isABTestActive) {
            setNextStep(CustomCampaignSteps.SETUP_A_B_TEST);
        } else {
            setNextStep(CustomCampaignSteps.SELECT_CONTENT);
        }
    }, [isABTestActive, setNextStep]);

    useEffect(() => {
        setIsContinueEnabled(Boolean(cohortId) || Boolean(segmentId));
    }, [cohortId, segmentId, setIsContinueEnabled]);

    // Force audience type based on platform integration and access control settings
    useEffect(() => {
        if (shouldShowOnlyCohorts && audienceType !== AudienceType.COHORT) {
            setAudienceType(AudienceType.COHORT);
        } else if (shouldShowOnlyAudiences && audienceType !== AudienceType.AUDIENCE) {
            setAudienceType(AudienceType.AUDIENCE);
        }
    }, [shouldShowOnlyCohorts, shouldShowOnlyAudiences, audienceType]);

    // Auto-disable A/B testing when conditions are no longer met
    // Only disable if we have sufficient data to make the determination
    useEffect(() => {
        if (isABTestActive && !isABTestingFullyAvailable) {
            // Only auto-disable if we have loaded the necessary data and can be certain the conditions aren't met
            const hasLoadedAudienceData = selectedAudienceOption && !isAudienceCountFetching;
            const isDefinitelyInvalidAudienceType = audienceType !== AudienceType.AUDIENCE;
            const isDefinitelyWrongChannel = channel !== CampaignChannels.EMAIL;
            const isDefinitelyInsufficientAudience = audienceCountNumber <= 1000 && audienceCountNumber !== undefined;

            // Only disable if we're certain the conditions can't be met (not due to loading state)
            if (
                isDefinitelyInvalidAudienceType ||
                isDefinitelyWrongChannel ||
                (hasLoadedAudienceData && isDefinitelyInsufficientAudience)
            ) {
                dispatchCampaignAction(removeABTestConfig());
                dispatchCampaignAction(resetSends());
                setIsABTestActive(false);
            }
        }
    }, [
        isABTestActive,
        isABTestingFullyAvailable,
        dispatchCampaignAction,
        selectedAudienceOption,
        isAudienceCountFetching,
        audienceType,
        channel,
        audienceCountNumber,
    ]);

    // Auto-switch to 100% when 50% option becomes unavailable
    useEffect(() => {
        if (testPercentage === '50' && !is50PercentSplitAvailable && audienceCountNumber !== undefined) {
            setTestPercentage('100');
            dispatchCampaignAction(setABTestPercentage(100));
        }
    }, [testPercentage, is50PercentSplitAvailable, audienceCountNumber, dispatchCampaignAction]);

    useEffect(() => {
        const updateSelectedAudience = (
            options: AutocompleteOption<number | string>[],
            segmentOrCohortId: string | number
        ) => {
            const filteredAudience = options.filter((option) => option.value === segmentOrCohortId)[0];
            const filteredAudienceOption = {
                index: 1,
                label: filteredAudience?.label,
                value: segmentOrCohortId,
            };

            setSelectedAudienceOption(filteredAudienceOption);
            setInputValue(filteredAudienceOption.label);
        };

        if (
            (shouldShowOnlyAudiences || (shouldShowBothOptions && audienceType === AudienceType.AUDIENCE)) &&
            isAudiencesSuccess
        ) {
            // If found, moves the "All Contacts" audience to the beginning of the audienceDropdownItems array state.
            // If not found, doesn't add 'undefined' to reorderedAudiences.
            const allAudience = audiences.find((audience) => audience.name === 'All Contacts');
            const otherAudience = audiences.filter((audience) => audience.name !== 'All Contacts');
            const reorderedAudiences = [...(allAudience ? [allAudience] : []), ...otherAudience];

            const dropdownItemsWithoutInvalidRelativeDates: Partial<Segment>[] = reorderedAudiences.filter(
                (audience) => audience && !hasInvalidRelativeDates(audience)
            );

            // Adds a "Create new" item to the beginning of the audienceDropdownItems array state.
            const dropdownItemsWithCreateNew = [
                { index: 0, label: 'Create new', value: null },
                ...dropdownItemsWithoutInvalidRelativeDates
                    .filter((audience) => audience && audience.id) // Additional safety check
                    .map((audience, index) => {
                        let label: string;
                        if (campaignState.isMixedChannel) {
                            const isSelected = audience.id === segmentId;
                            const isLoading = isMixedCountFetching;
                            const isError = isMixedCountError;
                            if (!isSelected || isLoading || isError) {
                                label = audience.name || '';
                            } else {
                                const emailCount = Number(mixedAudienceCountData?.email ?? 0);
                                const smsCount = Number(mixedAudienceCountData?.sms ?? 0);
                                label = `${audience.name} (${emailCount} email, ${smsCount} SMS contacts)`;
                            }
                        } else {
                            label = getAudienceLabel({
                                audience,
                                channel: CampaignChannelLabels[channel],
                                count: audienceCountData?.count,
                                isError: isAudienceCountError,
                                isLoading: isAudienceCountFetching,
                                selectedAudienceId: segmentId,
                            });
                        }

                        return {
                            index: index + 1,
                            label,
                            value: audience.id,
                        };
                    }),
            ];
            setAudienceDropdownItems(dropdownItemsWithCreateNew);

            if (segmentId) {
                updateSelectedAudience(dropdownItemsWithCreateNew, segmentId);
            }
        } else if (
            (shouldShowOnlyCohorts || (shouldShowBothOptions && audienceType === AudienceType.COHORT)) &&
            isCohortsSuccess
        ) {
            const cohortsOptions = cohorts?.data
                ?.filter((audience) => audience && audience.id) // Safety check for undefined/null audiences
                ?.map((audience) => {
                    const label = getAudienceLabel({
                        audience,
                        channel: CampaignChannelLabels[channel],
                        count: cohortData?.totalPatients,
                        isError: isCohortError,
                        isLoading: isCohortFetching,
                        selectedAudienceId: cohortId,
                    });

                    return {
                        label,
                        value: audience.id,
                    };
                });
            setAudienceDropdownItems(cohortsOptions);

            if (cohortId) {
                updateSelectedAudience(cohortsOptions, cohortId);
            }
        }
    }, [
        audiences,
        channel,
        isAudienceCountError,
        isAudienceCountFetching,
        isAudiencesSuccess,
        segmentId,
        audienceCountData,
        isCohortsSuccess,
        cohorts?.data,
        cohortId,
        audienceType,
        isCohortError,
        isCohortFetching,
        cohortData?.totalPatients,
        shouldShowOnlyCohorts,
        shouldShowOnlyAudiences,
        shouldShowBothOptions,
        mixedAudienceCountData,
        isMixedCountFetching,
        isMixedCountError,
        campaignState.isMixedChannel,
    ]);

    const handleAudienceTypeChange = (newType: AudienceType) => {
        setAudienceType(newType);

        setAudienceDropdownItems([]);
        setInputValue('');
        setSelectedAudienceOption(null);
        setErrorMessage(null);

        // Disable A/B testing if switching to cohort type
        if (newType === AudienceType.COHORT) {
            dispatchCampaignAction(selectAudience(null));
            if (isABTestActive) {
                dispatchCampaignAction(removeABTestConfig());
                dispatchCampaignAction(resetSends());
                setIsABTestActive(false);
            }
        } else {
            dispatchCampaignAction(setCohortId(null));
        }
    };

    const handleAudienceSelection = (option?: AutocompleteOption<number | string>) => {
        if (!option) {
            dispatchCampaignAction(selectAudience(null));
            dispatchCampaignAction(setCohortId(null));
            setSelectedAudienceOption(null);
            return;
        }
        if (shouldShowOnlyCohorts || (shouldShowBothOptions && audienceType === AudienceType.COHORT)) {
            dispatchCampaignAction(setCohortId(option.value as string));
            setSelectedAudienceOption(option);
        } else if (shouldShowOnlyAudiences || (shouldShowBothOptions && audienceType === AudienceType.AUDIENCE)) {
            dispatchCampaignAction(selectAudience(option.value as number));
            setSelectedAudienceOption(option);
        }
    };

    return (
        <FormLayout
            header="select audience"
            stepName="audience"
            icon={<SupervisedUserCircleOutlined />}
            guidingText="Select an audience from the dropdown to choose the contacts you want to connect with in this Curation."
        >
            <Stack spacing={4}>
                {shouldShowBothOptions && (
                    <AudienceTypeSelector
                        audienceType={audienceType}
                        onAudienceTypeChange={handleAudienceTypeChange}
                        disableCohort={shouldDisableCohort}
                        cohortTooltip={shouldShowCohortTooltip ? cohortContinualScheduleTooltip : ''}
                    />
                )}
                {errorMessage && (
                    <WarningSnackbar
                        warningMessage={errorMessage}
                        onClose={() => setErrorMessage(null)}
                        action={<Button onClick={() => refetchCohorts()}>Reload</Button>}
                    />
                )}
                <Stack spacing={0.5}>
                    <Autocomplete
                        data-test="custom-curation-audience-autocomplete"
                        freeSolo={false}
                        fullWidth
                        getOptionLabel={(option) => option.label ?? ''}
                        inputValue={inputValue ?? ''}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        label={
                            shouldShowOnlyCohorts
                                ? 'cohort'
                                : shouldShowOnlyAudiences
                                ? 'audience'
                                : audienceType.toLowerCase()
                        }
                        loading={isAudiencesLoading || isCohortsLoading}
                        onChange={(e: any, option) => handleAudienceSelection(option)}
                        onInputChange={(e: any, inputValue) => setInputValue(inputValue)}
                        options={audienceDropdownItems ?? []}
                        placeholder="Select"
                        renderOption={({ className, ...props }, option) => renderOption({ history, option, props })}
                        type="string"
                        value={selectedAudienceOption ?? null}
                    />
                    {(shouldShowOnlyCohorts || (shouldShowBothOptions && audienceType === AudienceType.COHORT)) && (
                        <Stack direction="row" columnGap={1}>
                            <InfoOutlined sx={{ fontSize: 14, mt: 0.75 }} />
                            <Typography variant="helperText">Showing only Public and Published Cohorts</Typography>
                        </Stack>
                    )}
                </Stack>

                {status === CampaignStatus.PAUSED && (
                    <InfoAlertBox text="This will apply to future sends only. Messages already sent will not be affected. Contacts from the original audience will be excluded automatically." />
                )}

                {/* A/B Test Section */}
                {/* A/B Test Toggle - Show only for single-channel email campaigns; mixed-channel
                    curations span multiple channel types so A/B testing is not supported. */}
                {canCurationsAbTesting && channel === CampaignChannels.EMAIL && !campaignState.isMixedChannel && (
                    <Stack spacing={2}>
                        <FormControlLabel
                            data-testid="ab-test-toggle"
                            control={
                                <Switch
                                    color="secondary"
                                    size="small"
                                    checked={isABTestActive}
                                    disabled={!isABTestingFullyAvailable || disableABTestEditing}
                                    onChange={(e) => {
                                        const newState = !isABTestActive;

                                        if (!isABTestActive) {
                                            // Currently inactive, so enable AB testing
                                            // Reset sends first to clear any existing flow (time delays, conditional splits, etc.)
                                            // that would be incompatible with A/B testing's expected send structure
                                            dispatchCampaignAction(resetSends());
                                            const initialABTestConfig: ABTestConfig = {
                                                testPercentage: 100,
                                            };
                                            dispatchCampaignAction(setABTestConfig(initialABTestConfig));
                                        } else {
                                            // Currently active, so disable AB testing
                                            dispatchCampaignAction(removeABTestConfig());
                                            dispatchCampaignAction(resetSends());
                                        }
                                        setIsABTestActive(newState);
                                    }}
                                    sx={{ marginLeft: -1 }}
                                />
                            }
                            label={
                                <Stack direction="row" alignItems="center" columnGap={0.75}>
                                    <Typography
                                        variant="body2"
                                        color={
                                            !isABTestingFullyAvailable || disableABTestEditing
                                                ? 'text.disabled'
                                                : 'text.primary'
                                        }
                                    >
                                        Enable A/B test
                                    </Typography>
                                    <Tooltip
                                        title={disableABTestEditing ? editCurationABTestTooltip : abTestTooltipMessage}
                                        sx={{
                                            pointerEvents: 'auto',
                                        }}
                                    >
                                        <Stack sx={{ fontSize: 14, marginTop: 0.5 }}>
                                            <InfoOutlined
                                                color={
                                                    !isABTestingFullyAvailable || disableABTestEditing
                                                        ? 'disabled'
                                                        : 'inherit'
                                                }
                                                fontSize="inherit"
                                            />
                                        </Stack>
                                    </Tooltip>
                                </Stack>
                            }
                            sx={{ cursor: !isABTestingFullyAvailable || disableABTestEditing ? 'default' : 'pointer' }}
                        />

                        {/* Show A/B test configuration only when A/B test is active */}
                        {isABTestActive && (
                            <Stack spacing={2}>
                                <Typography variant="body2">Percentage of Audience to Test</Typography>

                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        value={testPercentage}
                                        onChange={(e) => {
                                            const newPercentage = e.target.value as '50' | '100';
                                            setTestPercentage(newPercentage);
                                            dispatchCampaignAction(setABTestPercentage(parseInt(newPercentage, 10)));
                                        }}
                                    >
                                        <Tooltip
                                            title={
                                                is50PercentSplitAvailable ? '' : insufficientAudienceFor50PercentSplit
                                            }
                                            placement="top"
                                        >
                                            <span>
                                                <FormControlLabel
                                                    value="50"
                                                    control={<Radio size="small" />}
                                                    label="50%"
                                                    disabled={!is50PercentSplitAvailable}
                                                />
                                            </span>
                                        </Tooltip>
                                        <FormControlLabel value="100" control={<Radio size="small" />} label="100%" />
                                    </RadioGroup>
                                </FormControl>

                                {/* Conditional display based on test percentage */}
                                <Stack spacing={1.4}>
                                    {(() => {
                                        const remainingCount = audienceCountNumber
                                            ? Math.floor(audienceCountNumber * 0.5)
                                            : null;
                                        const variantACount = audienceCountNumber
                                            ? Math.floor(audienceCountNumber * (testPercentage === '50' ? 0.25 : 0.5))
                                            : null;
                                        const variantBCount =
                                            audienceCountNumber && variantACount !== null
                                                ? testPercentage === '50'
                                                    ? audienceCountNumber - variantACount - (remainingCount ?? 0)
                                                    : audienceCountNumber - variantACount
                                                : null;

                                        return (
                                            <>
                                                <AudienceSplitRow
                                                    label="A"
                                                    text="Email A"
                                                    percentage={testPercentage === '50' ? '25%' : '50%'}
                                                    contactCount={variantACount}
                                                />

                                                <AudienceSplitRow
                                                    label="B"
                                                    text="Email B"
                                                    percentage={testPercentage === '50' ? '25%' : '50%'}
                                                    contactCount={variantBCount}
                                                />

                                                {testPercentage === '50' && (
                                                    <AudienceSplitRow
                                                        label=""
                                                        text="Remaining"
                                                        percentage="50%"
                                                        contactCount={remainingCount}
                                                    />
                                                )}
                                            </>
                                        );
                                    })()}
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignAudienceStep;
